(() => {
  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const closeTimers = new WeakMap();
  const desktopMedia = window.matchMedia('(min-width: 1024px)');

  const syncBodyScroll = () => {
    document.body.classList.toggle('hv-no-scroll', Boolean(document.querySelector('[data-hv-panel].is-open')));
  };

  const getCssDuration = (panel, property, fallback) => {
    if (reducedMotion) return 0;
    const rootNode = panel.closest('[data-hv-header-root]');
    const duration = parseFloat(getComputedStyle(rootNode).getPropertyValue(property));
    return Number.isFinite(duration) ? duration : fallback;
  };

  const getPanelDuration = (panel) => getCssDuration(panel, '--hv00-panel-duration', 700);
  const getCloseDuration = (panel) => Math.max(getPanelDuration(panel), getCssDuration(panel, '--hv00-overlay-duration', 500));

  const getFocusable = (panel) => Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
    .filter((node) => node.offsetParent !== null && !node.closest('[aria-hidden="true"]'));

  const resetMenu = (panel) => {
    panel.dataset.hvOpenLevel = '1';
    panel.querySelectorAll('[data-hv-desktop-panel]').forEach((column) => {
      column.classList.remove('is-active', 'has-selection');
      column.setAttribute('aria-hidden', 'true');
    });
    panel.querySelectorAll('[data-hv-desktop-column]').forEach((column) => column.classList.remove('has-selection'));
    panel.querySelectorAll('[data-hv-desktop-trigger]').forEach((trigger) => {
      trigger.classList.remove('is-selected');
      trigger.setAttribute('aria-expanded', 'false');
    });
    panel.querySelectorAll('.hv00-menu-link-group').forEach((group) => group.classList.remove('is-selected'));
    panel.querySelectorAll('[data-hv-mobile-branch]').forEach((branch) => branch.classList.remove('is-active'));
    panel.querySelectorAll('[data-hv-mobile-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    panel.querySelectorAll('.hv00-mobile-link-group').forEach((group) => group.classList.remove('is-active'));
  };

  const closePanel = (panel, restoreFocus = true) => {
    if (!panel || panel.hidden) return;
    clearTimeout(closeTimers.get(panel));
    const closeDuration = getCloseDuration(panel);
    panel.style.setProperty('--hv00-close-duration', `${closeDuration}ms`);
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    const timer = window.setTimeout(() => {
      panel.hidden = true;
      if (panel.matches('[data-hv-menu-panel]')) resetMenu(panel);
      syncBodyScroll();
      if (restoreFocus) panel._hvReturnFocus?.focus();
    }, closeDuration);
    closeTimers.set(panel, timer);
  };

  const openPanel = (panel, trigger) => {
    if (!panel) return;
    document.querySelectorAll('[data-hv-panel].is-open').forEach((openLayer) => {
      if (openLayer !== panel) closePanel(openLayer, false);
    });
    clearTimeout(closeTimers.get(panel));
    if (panel.matches('[data-hv-menu-panel]')) resetMenu(panel);
    panel._hvReturnFocus = trigger;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    panel.getBoundingClientRect();
    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      syncBodyScroll();
      window.setTimeout(() => {
        const firstFocus = panel.querySelector('.hv00-search__input') || getFocusable(panel)[0];
        firstFocus?.focus();
      }, reducedMotion ? 0 : 80);
    });
  };

  const clearDesktopFrom = (menuPanel, panelLevel) => {
    menuPanel.querySelectorAll('[data-hv-desktop-panel]').forEach((column) => {
      if (Number(column.dataset.hvLevel) < panelLevel) return;
      column.classList.remove('is-active', 'has-selection');
      column.setAttribute('aria-hidden', 'true');
    });
    menuPanel.querySelectorAll('[data-hv-desktop-trigger]').forEach((trigger) => {
      if (Number(trigger.dataset.hvLevel) < panelLevel - 1) return;
      trigger.classList.remove('is-selected');
      trigger.setAttribute('aria-expanded', 'false');
    });
    menuPanel.querySelectorAll('.hv00-menu-link-group').forEach((group) => {
      const trigger = group.querySelector('[data-hv-desktop-trigger]');
      if (!trigger || Number(trigger.dataset.hvLevel) < panelLevel - 1) return;
      group.classList.remove('is-selected');
    });
    menuPanel.querySelectorAll('[data-hv-desktop-column], [data-hv-desktop-panel]').forEach((column) => {
      if (Number(column.dataset.hvLevel) >= panelLevel - 1) column.classList.remove('has-selection');
    });
  };

  const activateDesktopTrigger = (menuPanel, trigger) => {
    const level = Number(trigger.dataset.hvLevel);
    const target = document.getElementById(trigger.dataset.hvTarget);
    if (!target) return;
    clearDesktopFrom(menuPanel, level + 1);
    menuPanel.dataset.hvOpenLevel = target.dataset.hvLevel;
    trigger.classList.add('is-selected');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.closest('.hv00-menu-link-group')?.classList.add('is-selected');
    trigger.closest('[data-hv-desktop-column], [data-hv-desktop-panel]')?.classList.add('has-selection');
    target.classList.add('is-active');
    target.setAttribute('aria-hidden', 'false');
  };

  const clearDesktopForLeaf = (menuPanel, leaf) => {
    const level = Number(leaf.dataset.hvLevel);
    clearDesktopFrom(menuPanel, level + 1);
    menuPanel.dataset.hvOpenLevel = String(level);
  };

  const collapseMobileItem = (item) => {
    item.querySelectorAll('[data-hv-mobile-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    item.querySelectorAll('[data-hv-mobile-branch]').forEach((branch) => branch.classList.remove('is-active'));
    item.querySelectorAll('.hv00-mobile-link-group').forEach((group) => group.classList.remove('is-active'));
  };

  const toggleMobileBranch = (trigger) => {
    const item = trigger.closest('.hv00-mobile-item');
    const group = trigger.closest('.hv00-mobile-link-group');
    const branch = group ? group.nextElementSibling : trigger.nextElementSibling;
    if (!item || !branch?.matches('[data-hv-mobile-branch]')) return;
    const opening = !branch.classList.contains('is-active');
    Array.from(item.parentElement.children).forEach((sibling) => {
      if (sibling !== item) collapseMobileItem(sibling);
    });
    if (!opening) collapseMobileItem(item);
    branch.classList.toggle('is-active', opening);
    trigger.setAttribute('aria-expanded', String(opening));
    group?.classList.toggle('is-active', opening);
  };

  const initializeHeader = (rootNode) => {
    if (rootNode.dataset.hvHeaderReady === 'true') return;
    rootNode.dataset.hvHeaderReady = 'true';
    const header = rootNode.querySelector('[data-hv-header]');
    const menuPanel = rootNode.querySelector('[data-hv-menu-panel]');
    const searchPanel = rootNode.querySelector('[data-hv-search-panel]');
    const isInnerPage = rootNode.dataset.hvInnerPage === 'true';
    const innerHideDistance = Math.max(0, Number(rootNode.dataset.hvInnerHideDistance) || 0);

    rootNode.addEventListener('click', (event) => {
      const menuOpen = event.target.closest('[data-hv-menu-open]');
      const searchOpen = event.target.closest('[data-hv-search-open]');
      const closeTrigger = event.target.closest('[data-hv-panel-close], [data-hv-panel-shade]');
      const desktopTrigger = event.target.closest('[data-hv-desktop-trigger]');
      const mobileTrigger = event.target.closest('[data-hv-mobile-toggle]');

      if (menuOpen) openPanel(menuPanel, menuOpen);
      if (searchOpen) openPanel(searchPanel, searchOpen);
      if (closeTrigger) closePanel(closeTrigger.closest('[data-hv-panel]'));
      if (desktopTrigger && desktopMedia.matches) activateDesktopTrigger(menuPanel, desktopTrigger);
      if (mobileTrigger && !desktopMedia.matches) toggleMobileBranch(mobileTrigger);
    });

    rootNode.addEventListener('mouseover', (event) => {
      if (!desktopMedia.matches) return;
      const desktopTrigger = event.target.closest('[data-hv-desktop-trigger]');
      const desktopParent = event.target.closest('[data-hv-desktop-parent]');
      const desktopLeaf = event.target.closest('[data-hv-desktop-leaf]');
      if (desktopTrigger) activateDesktopTrigger(menuPanel, desktopTrigger);
      if (!desktopTrigger && desktopParent) {
        const parentTrigger = desktopParent.querySelector('[data-hv-desktop-trigger]');
        if (parentTrigger) activateDesktopTrigger(menuPanel, parentTrigger);
      }
      if (desktopLeaf) clearDesktopForLeaf(menuPanel, desktopLeaf);
    });

    let previousY = window.scrollY;
    let ticking = false;
    const updateHeader = () => {
      const currentY = window.scrollY;
      const hideDistance = isInnerPage ? innerHideDistance : 180;
      const shouldHide = header.dataset.hvHideOnScroll === 'true' && currentY > hideDistance && currentY > previousY;
      header.classList.toggle('hv00-header--scrolled', currentY > 36);
      header.classList.toggle('hv00-header--hidden', shouldHide);
      previousY = currentY;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateHeader);
    }, { passive: true });
    updateHeader();
  };

  document.querySelectorAll('[data-hv-header-root]').forEach(initializeHeader);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-hv-header-root]').forEach(initializeHeader);
  });

  document.addEventListener('keydown', (event) => {
    const activePanel = document.querySelector('[data-hv-panel].is-open');
    if (!activePanel) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(activePanel);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(activePanel);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const initializeHero = (hero) => {
    if (hero.dataset.hvHeroReady === 'true') return;
    hero.dataset.hvHeroReady = 'true';
    const slides = Array.from(hero.querySelectorAll('[data-hv-hero-slide]'));
    const dots = Array.from(hero.querySelectorAll('[data-hv-hero-dot]'));
    if (!slides.length) return;
    const shouldAutoplay = hero.dataset.autoplay === 'true' && !reducedMotion;
    const stackScale = Math.max(0, Number(hero.dataset.stackScale) || 0) / 100;
    let stackTicking = false;

    const visibleVideos = (slide) => Array.from(slide.querySelectorAll('[data-hv-hero-video]'))
      .filter((video) => video.offsetParent !== null);

    const updateVideoToggle = (slide) => {
      const button = slide.querySelector('[data-hv-video-toggle]');
      if (!button) return;
      const playing = visibleVideos(slide).some((video) => !video.paused && !video.ended);
      button.classList.toggle('is-paused', !playing);
      button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
    };

    const activate = (index) => {
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.querySelectorAll('[data-hv-hero-video]').forEach((video) => {
          if (active && shouldAutoplay && video.offsetParent !== null) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
        updateVideoToggle(slide);
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const updateStack = () => {
      slides.forEach((slide) => {
        const sticky = slide.querySelector('[data-hv-hero-sticky]');
        if (!sticky) return;
        const rect = slide.getBoundingClientRect();
        const travel = Math.max(1, slide.offsetHeight - window.innerHeight);
        const exitProgress = Math.max(0, Math.min(1, -rect.top / travel));
        sticky.style.setProperty('--hv10-exit-scale', (1 - (exitProgress * stackScale)).toFixed(4));
      });
      stackTicking = false;
    };

    const requestStackUpdate = () => {
      if (stackTicking) return;
      stackTicking = true;
      requestAnimationFrame(updateStack);
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        slides[index]?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      activate(slides.indexOf(visible.target));
    }, { threshold: [0.35, 0.55, 0.75] });

    slides.forEach((slide) => observer.observe(slide));
    hero.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-hv-video-toggle]');
      if (!toggle) return;
      const slide = toggle.closest('[data-hv-hero-slide]');
      const videos = visibleVideos(slide);
      const shouldPlay = videos.every((video) => video.paused || video.ended);
      videos.forEach((video) => {
        if (shouldPlay) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
      updateVideoToggle(slide);
    });
    hero.querySelectorAll('[data-hv-hero-video]').forEach((video) => {
      ['play', 'pause', 'ended'].forEach((eventName) => {
        video.addEventListener(eventName, () => updateVideoToggle(video.closest('[data-hv-hero-slide]')));
      });
    });
    window.addEventListener('scroll', requestStackUpdate, { passive: true });
    window.addEventListener('resize', requestStackUpdate);
    activate(0);
    updateStack();
  };

  document.querySelectorAll('[data-hv-hero]').forEach(initializeHero);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-hv-hero]').forEach(initializeHero);
  });
  document.addEventListener('shopify:block:select', (event) => {
    event.target.closest('[data-hv-hero-slide]')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  });

  const initializeSplitCarousel = (carousel) => {
    if (carousel.dataset.hvCarouselReady === 'true') return;
    carousel.dataset.hvCarouselReady = 'true';
    const slides = Array.from(carousel.querySelectorAll('[data-hv-carousel-slide]'));
    const dots = Array.from(carousel.querySelectorAll('[data-hv-carousel-dot]'));
    const cursor = carousel.querySelector('[data-hv-carousel-cursor]');
    const media = carousel.querySelector('.hv30-carousel__media');
    const interval = Math.max(2000, Number(carousel.dataset.interval) || 5500);
    if (!slides.length) return;
    let index = 0;
    let timer;

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
    };

    const restart = () => {
      if (carousel.dataset.autoplay !== 'true' || reducedMotion) return;
      clearInterval(timer);
      timer = setInterval(() => show(index + 1), interval);
    };

    dots.forEach((dot, dotIndex) => {
      dot.addEventListener('click', () => {
        show(dotIndex);
        restart();
      });
    });

    carousel.querySelector('[data-hv-carousel-prev]')?.addEventListener('click', () => {
      show(index - 1);
      restart();
    });
    carousel.querySelector('[data-hv-carousel-next]')?.addEventListener('click', () => {
      show(index + 1);
      restart();
    });

    media?.addEventListener('mousemove', (event) => {
      if (!cursor || window.innerWidth < 1024) return;
      const rect = media.getBoundingClientRect();
      cursor.style.transform = `translate3d(${event.clientX - rect.left - (cursor.offsetWidth / 2)}px, ${event.clientY - rect.top - (cursor.offsetHeight / 2)}px, 0)`;
    });

    show(0);
    restart();
  };

  document.querySelectorAll('[data-hv-split-carousel]').forEach(initializeSplitCarousel);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-hv-split-carousel]').forEach(initializeSplitCarousel);
  });

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.classList.add('is-revealed');
    });
  }, { threshold: 0.14 });

  const observeReveals = (scope) => {
    const nodes = [];
    if (scope.matches?.('[data-hv-reveal]')) nodes.push(scope);
    scope.querySelectorAll?.('[data-hv-reveal]').forEach((node) => nodes.push(node));
    nodes.forEach((node) => {
      if (node.dataset.hvRevealReady === 'true') return;
      node.dataset.hvRevealReady = 'true';
      revealObserver.observe(node);
    });
  };

  const parallaxNodes = new Set();
  let parallaxTicking = false;

  const updateParallax = () => {
    parallaxNodes.forEach((node) => {
      if (!node.isConnected || !node.parentElement) {
        parallaxNodes.delete(node);
        return;
      }
      const rect = node.parentElement.getBoundingClientRect();
      const distance = Math.max(0, Number(node.dataset.hvParallaxDistance) || 0);
      const scale = Math.max(1, Number(node.dataset.hvParallaxScale) || 1);
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      const offset = reducedMotion ? 0 : (-distance + (progress * distance));
      node.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0) scale(${reducedMotion ? '1' : scale.toFixed(4)})`;
    });
    parallaxTicking = false;
  };

  const requestParallaxUpdate = () => {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(updateParallax);
  };

  const initializeParallax = (scope) => {
    const nodes = [];
    if (scope.matches?.('[data-hv-parallax]')) nodes.push(scope);
    scope.querySelectorAll?.('[data-hv-parallax]').forEach((node) => nodes.push(node));
    nodes.forEach((node) => {
      if (node.dataset.hvParallaxReady === 'true') return;
      node.dataset.hvParallaxReady = 'true';
      parallaxNodes.add(node);
    });
    requestParallaxUpdate();
  };

  observeReveals(document);
  initializeParallax(document);
  document.addEventListener('shopify:section:load', (event) => {
    observeReveals(event.target);
    initializeParallax(event.target);
  });
  if (!reducedMotion) {
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
    window.addEventListener('resize', requestParallaxUpdate);
  }
})();
